import React, { useEffect, useState } from 'react';
import { buildEmailTemplate } from '../../templates/emailTemplates';
import { client } from '../utils/api';

const USERS_CONTROLLER = 'usersController.php';
const EVENTS_CONTROLLER = 'eventsController.php';
const DOCUMENT_CONTROLLER = 'documentController.php';
const EMAIL_CONTROLLER = 'emailController.php';
const BASE_URL = process.env.REACT_APP_DB_BASE_URL || 'http://localhost/api';
const DOCUMENT_TABS = [
  { id: 'receipt-of-payment', label: 'Receipt of Payment' },
];

const getToday = () => {
  const today = new Date();
  today.setMinutes(today.getMinutes() - today.getTimezoneOffset());
  return today.toISOString().slice(0, 10);
};

const parseCurrencyAmount = (value, defaultCurrency = 'CHF') => {
  let normalized = String(value ?? '').trim();
  let currency = defaultCurrency;
  const prefix = normalized.match(/^([a-z]{3})\s*/i);
  if (prefix) {
    currency = prefix[1].toUpperCase();
    normalized = normalized.slice(prefix[0].length);
  }

  const suffix = normalized.match(/\s*([a-z]{3})$/i);
  if (suffix) {
    const suffixCurrency = suffix[1].toUpperCase();
    if (prefix && suffixCurrency !== currency) {
      return { amount: Number.NaN, currency };
    }
    currency = suffixCurrency;
    normalized = normalized.slice(0, -suffix[0].length);
  }

  normalized = normalized.replace(/[\s'’]/g, '');
    if (/,[0-9]{1,2}$/.test(normalized) && !normalized.includes('.')) {
    normalized = normalized.replace(',', '.');
  } else {
    normalized = normalized.replace(/,/g, '');
  }
  return { amount: normalized ? Number(normalized) : Number.NaN, currency };
};

const toBase64 = async (blob) => {
  const reader = new FileReader();
  const dataUrl = await new Promise((resolve, reject) => {
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Could not prepare the receipt attachment.'));
    reader.readAsDataURL(blob);
  });
  return String(dataUrl).split(',')[1] || '';
};

const decodeBase64Pdf = (encodedPdf) => {
  const base64 = String(encodedPdf).replace(/^data:application\/pdf;base64,/, '');
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Blob([bytes], { type: 'application/pdf' });
};

const getUserName = (user) => user?.fullName || [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || 'Selected user';

export default function DocumentCreationManager() {
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [activeTab, setActiveTab] = useState(DOCUMENT_TABS[0].id);
  const [form, setForm] = useState({
    userId: '',
    eventId: '',
    purchaseDate: getToday(),
    pricePaid: '',
    discount: '0',
  });
  const [emailRecipient, setEmailRecipient] = useState('');
  const [generatedReceipt, setGeneratedReceipt] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  useEffect(() => {
    let isMounted = true;
    Promise.all([client.get(USERS_CONTROLLER), client.get(EVENTS_CONTROLLER)])
      .then(([userResponse, eventResponse]) => {
        if (!isMounted) return;
        setUsers(userResponse.users || []);
        setEvents(eventResponse.events || []);
      })
      .catch((loadError) => {
        if (isMounted) setError(`Failed to load users and events: ${loadError.message}`);
      })
      .finally(() => {
        if (isMounted) setIsLoadingOptions(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => () => {
    if (generatedReceipt?.url) URL.revokeObjectURL(generatedReceipt.url);
  }, [generatedReceipt?.url]);

  const selectedUser = users.find((user) => String(user.id) === String(form.userId));
  const selectedEvent = events.find((event) => String(event.id) === String(form.eventId));

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setGeneratedReceipt(null);
    setSuccess('');
  };

  const handleGenerateReceipt = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    setGeneratedReceipt(null);

    if (!selectedUser || !selectedEvent) {
      setError('Select a user and event before generating the receipt.');
      return;
    }

    const userEmail = String(selectedUser.email || '').trim();
    if (!userEmail) {
      setError('The selected user does not have an email address.');
      return;
    }

    const priceInput = parseCurrencyAmount(form.pricePaid);
    const discountInput = form.discount.trim()
      ? parseCurrencyAmount(form.discount, priceInput.currency)
      : { amount: 0, currency: priceInput.currency };
    const pricePaid = priceInput.amount;
    const discount = discountInput.amount;
    if (!Number.isFinite(pricePaid) || pricePaid < 0 || !Number.isFinite(discount) || discount < 0) {
      setError('Price paid and discount must be valid non-negative amounts with a three-letter currency code.');
      return;
    }
    if (discountInput.currency !== priceInput.currency) {
      setError('Price paid and discount must use the same currency.');
      return;
    }

    try {
      setIsGenerating(true);
      const response = await fetch(`${BASE_URL}/${DOCUMENT_CONTROLLER}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documentType: 'receipt_of_payment',
          name: getUserName(selectedUser),
          email: userEmail,
          product: selectedEvent.title || 'Event',
          userId: Number(form.userId),
          eventId: Number(form.eventId),
          date: form.purchaseDate,
          purchaseDate: form.purchaseDate,
          dateOfPurchase: form.purchaseDate,
          price: form.pricePaid.trim(),
          pricePaid,
          discount,
          currency: priceInput.currency,
        }),
      });

      let pdfBlob;
      let fileName;
      const contentType = response.headers.get('content-type') || '';

      if (contentType.includes('application/json')) {
        const result = await response.json();
        if (!response.ok || result.ok === false) {
          throw new Error(result.error || `HTTP error! status: ${response.status}`);
        }
        const encodedPdf = result.pdfBase64 || result.documentBase64 || result.base64;
        if (!encodedPdf) {
          throw new Error('The document controller did not return a PDF file.');
        }
        pdfBlob = decodeBase64Pdf(encodedPdf);
        fileName = result.fileName || result.filename;
      } else {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        pdfBlob = await response.blob();
      }

      if (!pdfBlob.size) {
        throw new Error('The generated receipt is empty.');
      }

      const safeUserId = String(form.userId).replace(/[^a-zA-Z0-9_-]/g, '');
      const receiptFileName = fileName || `receipt-${safeUserId}-${form.purchaseDate}.pdf`;
      setGeneratedReceipt({
        blob: pdfBlob,
        fileName: receiptFileName,
        url: URL.createObjectURL(pdfBlob),
        eventTitle: selectedEvent.title || 'Event',
      });
      setEmailRecipient(selectedUser.email || '');
      setSuccess('Receipt generated. You can preview, download, or email it to the selected user.');
    } catch (generationError) {
      setError(generationError.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSendReceipt = async () => {
    const recipient = emailRecipient.trim();
    if (!generatedReceipt || !recipient) {
      setError('Generate a receipt and enter a recipient email address first.');
      return;
    }

    setError('');
    setSuccess('');
    try {
      setIsSendingEmail(true);
      const attachmentContent = await toBase64(generatedReceipt.blob);
      const subject = `Receipt of Payment - ${generatedReceipt.eventTitle}`;
      const response = await fetch(`${BASE_URL}/${EMAIL_CONTROLLER}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject,
          htmlBody: buildEmailTemplate('RPSwiss', subject, 'Please find your receipt of payment attached.'),
          recipients: [recipient],
          attachments: [{
            filename: generatedReceipt.fileName,
            content: attachmentContent,
            contentType: 'application/pdf',
            encoding: 'base64',
          }],
        }),
      });
      const result = await response.json();
      if (!response.ok || !result.ok) {
        throw new Error(result.error || `HTTP error! status: ${response.status}`);
      }

      setSuccess(`Receipt emailed to ${recipient}.`);
    } catch (emailError) {
      setError(emailError.message);
    } finally {
      setIsSendingEmail(false);
    }
  };

  return (
    <div className="admin-view-wrapper">
      <div className="admin-view-header">
        <h2 className="admin-title">Create Documents</h2>
        {error && <div className="admin-error">{error}</div>}
        {success && <div className="admin-success">{success}</div>}
      </div>

      <div role="tablist" aria-label="Document types" style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        {DOCUMENT_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            className="btn btn-secondary"
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'receipt-of-payment' && (
        <>
          <form onSubmit={handleGenerateReceipt} className="admin-form-container grid-2-col">
            <div className="form-section-header" style={{ gridColumn: 'span 2' }}>
              <h3 className="form-section-title">Receipt of Payment</h3>
            </div>

            <div className="form-group">
              <label htmlFor="receipt-user">User</label>
              <select
                id="receipt-user"
                required
                value={form.userId}
                onChange={(event) => updateForm('userId', event.target.value)}
                disabled={isLoadingOptions}
              >
                <option value="">Select a user</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>{getUserName(user)}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="receipt-event">Event</label>
              <select
                id="receipt-event"
                required
                value={form.eventId}
                onChange={(event) => updateForm('eventId', event.target.value)}
                disabled={isLoadingOptions}
              >
                <option value="">Select an event</option>
                {events.map((event) => (
                  <option key={event.id} value={event.id}>{event.title}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="receipt-purchase-date">Purchase date</label>
              <input
                id="receipt-purchase-date"
                type="date"
                required
                value={form.purchaseDate}
                onChange={(event) => updateForm('purchaseDate', event.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="receipt-price-paid">Price paid</label>
              <input
                id="receipt-price-paid"
                type="text"
                inputMode="decimal"
                required
                value={form.pricePaid}
                placeholder="12.35 CHF"
                onChange={(event) => updateForm('pricePaid', event.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="receipt-discount">Discount</label>
              <input
                id="receipt-discount"
                type="text"
                inputMode="decimal"
                value={form.discount}
                placeholder="12.35 CHF"
                onChange={(event) => updateForm('discount', event.target.value)}
              />
            </div>

            <div className="form-footer" style={{ gridColumn: 'span 2', justifyContent: 'flex-end' }}>
              <button type="submit" className="btn btn-submit-professionals" disabled={isLoadingOptions || isGenerating}>
                {isGenerating ? 'Generating...' : 'Generate receipt'}
              </button>
            </div>
          </form>

          {generatedReceipt && (
            <section className="admin-form-container" style={{ marginTop: '20px', padding: '18px' }}>
              <div className="panel-header">
                <h3>Generated receipt</h3>
              </div>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
                <a className="btn btn-submit-professionals" href={generatedReceipt.url} download={generatedReceipt.fileName}>
                  Download PDF
                </a>
                <div className="form-group" style={{ margin: 0, flex: '1 1 260px' }}>
                  <label htmlFor="receipt-email-recipient">Email receipt to</label>
                  <input
                    id="receipt-email-recipient"
                    type="email"
                    required
                    value={emailRecipient}
                    onChange={(event) => setEmailRecipient(event.target.value)}
                  />
                </div>
                <button type="button" className="btn btn-secondary" onClick={handleSendReceipt} disabled={isSendingEmail || !emailRecipient.trim()}>
                  {isSendingEmail ? 'Sending...' : 'Email receipt'}
                </button>
              </div>
              <iframe
                title="Receipt of Payment preview"
                src={generatedReceipt.url}
                style={{ width: '100%', height: '640px', border: '1px solid #dfe4e9', borderRadius: '6px' }}
              />
            </section>
          )}
        </>
      )}
    </div>
  );
}
